import { getWorkflowLogById, getWorkflowLogOperationLogs, getWorkflowLogOutput, getWorkflowLogs, getWorkflowLogsMetadata, getWorkflowSnapshots } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import { param } from '@/redux/selectors/uiSelectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useWorkflowLogsFilters from './lib/useWorkflowLogsFilters';

const WorkflowLogsModalStateHandler = () => {
  const { getPreparedFilters } = useWorkflowLogsFilters();
  const chartCancelRef = useRef();
  const lastFiltersKeyRef = useRef(null);
  const lastWorkflowIdRef = useRef(null);
  const lastWorkflowIdForLogsRef = useRef(null);
  const lastActiveLogIdRef = useRef(null);
  const hasFetchedSnapshotsRef = useRef(new Set());
  const hasFetchedLogDetailsRef = useRef(new Set());
  const hasFetchedLogOutputRef = useRef(new Set());
  const hasFetchedLogOperationLogsRef = useRef(new Set());
  const isFetchingRef = useRef(false);

  const workflowIdParam = useArgSelector(param, 'workflowId');
  const workflowIdQuery = useArgSelector(selectors.query, 'workflowId');
  const workflowId = workflowIdParam || workflowIdQuery;
  const activeLogId = useArgSelector(selectors.query, 'logId');

  const dateRangeFilter = useArgSelector(selectors.pref, `workflowLogsDateRangeFilter-${workflowId}`);
  const snapshotFilter = useArgSelector(selectors.pref, `workflowLogsSnapshotFilter-${workflowId}`);
  const environmentFilter = useArgSelector(selectors.pref, `workflowLogsEnvironmentFilter-${workflowId}`);
  const statusFilter = useArgSelector(selectors.pref, `workflowLogsStatusFilter-${workflowId}`);
  const activityFilter = useArgSelector(selectors.pref, `workflowLogsActivityFilter-${workflowId}`);
  const endUserFilter = useArgSelector(selectors.pref, `workflowLogsEndUserFilter-${workflowId}`);
  const installationFilter = useArgSelector(selectors.pref, `workflowLogsInstallationFilter-${workflowId}`);
  const searchValue = useArgSelector(selectors.query, 'search');

  const logOutput = useArgSelector(selectors.workflowLogOutputById, activeLogId);
  const workflowSnapshots = useSelector((state) => state.workflow?.workflowSnapshots || []);
  const runUpdateIsRunning = useSelector((state) => workflowScreenSelectors.workflowRunUpdate(state)?.isRunning);
  const runUpdateWorkflowId = useSelector((state) => workflowScreenSelectors.workflowRunUpdate(state)?.workflowId);
  const runUpdateLogId = useSelector((state) => workflowScreenSelectors.workflowRunUpdate(state)?.workflowLogId);
  const wasRunningRef = useRef(false);

  const filtersKey = JSON.stringify({
    dateRangeFilter,
    snapshotFilter,
    environmentFilter,
    statusFilter,
    activityFilter,
    endUserFilter,
    installationFilter,
    searchValue,
    workflowId
  });

  // Handle main logs, metadata, and chart fetching when filters or workflowId change
  useEffect(() => {
    if (!workflowId) {
      return;
    }

    if (lastFiltersKeyRef.current === filtersKey && lastWorkflowIdRef.current === workflowId) {
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    lastFiltersKeyRef.current = filtersKey;
    lastWorkflowIdRef.current = workflowId;

    const preparedFilters = getPreparedFilters();

    getWorkflowLogs({ workflowId, ...preparedFilters });
    getWorkflowLogsMetadata({ workflowId, ...preparedFilters });

    requestAnimationFrame(() => {
      isFetchingRef.current = false;
    });
  }, [workflowId, filtersKey, getPreparedFilters]);

  // Live pubsub run updates only carry a thin runtime snapshot, so when a run for
  // this workflow finishes while the modal is open (e.g. a replay), refetch the list
  // so its row shows final status, counts, and tags without requiring a click.
  useEffect(() => {
    const wasRunning = wasRunningRef.current;
    wasRunningRef.current = runUpdateIsRunning === true;

    if (!workflowId || !wasRunning || runUpdateIsRunning !== false) {
      return;
    }
    if (runUpdateWorkflowId && runUpdateWorkflowId !== workflowId) {
      return;
    }

    const preparedFilters = getPreparedFilters();
    getWorkflowLogs({ workflowId, ...preparedFilters });
    getWorkflowLogsMetadata({ workflowId, ...preparedFilters });

    if (runUpdateLogId && runUpdateLogId === activeLogId) {
      getWorkflowLogById({ workflowId, workflowLogId: activeLogId });
      getWorkflowLogOutput({ workflowId, logId: activeLogId });
    }
  }, [runUpdateIsRunning, runUpdateWorkflowId, runUpdateLogId, workflowId, activeLogId, getPreparedFilters]);

  // Handle snapshots fetching - only once per workflowId
  useEffect(() => {
    if (!workflowId) {
      return;
    }

    if (hasFetchedSnapshotsRef.current.has(workflowId)) {
      return;
    }

    const hasSnapshotsForWorkflow = workflowSnapshots.some((snapshot) => snapshot?.workflowId === workflowId);

    if (!hasSnapshotsForWorkflow) {
      getWorkflowSnapshots({ workflowId });
    }

    hasFetchedSnapshotsRef.current.add(workflowId);
  }, [workflowId]);

  // Handle individual log details fetching
  useEffect(() => {
    if (!workflowId || !activeLogId || activeLogId === 'summary') {
      return;
    }

    if (lastWorkflowIdForLogsRef.current !== workflowId && lastWorkflowIdForLogsRef.current !== null) {
      hasFetchedLogDetailsRef.current.clear();
      hasFetchedLogOutputRef.current.clear();
      hasFetchedLogOperationLogsRef.current.clear();
      lastActiveLogIdRef.current = null;
    }

    lastWorkflowIdForLogsRef.current = workflowId;

    if (lastActiveLogIdRef.current === activeLogId) {
      return;
    }

    lastActiveLogIdRef.current = activeLogId;

    const logKey = `${workflowId}-${activeLogId}`;
    const outputKey = `${workflowId}-${activeLogId}-output`;

    if (!hasFetchedLogDetailsRef.current.has(logKey)) {
      getWorkflowLogById({ workflowId, workflowLogId: activeLogId });
      hasFetchedLogDetailsRef.current.add(logKey);
    }

    if (!hasFetchedLogOutputRef.current.has(outputKey)) {
      getWorkflowLogOutput({ workflowId, logId: activeLogId });
      hasFetchedLogOutputRef.current.add(outputKey);
    }
  }, [workflowId, activeLogId]);

  useEffect(() => {
    const operationLogsKey = `${workflowId}-${activeLogId}-operation-logs`;
    if (!logOutput) return;
    if (hasFetchedLogOperationLogsRef.current.has(operationLogsKey)) return;
    if (!logOutput.operationLogs) {
      getWorkflowLogOperationLogs({ workflowId, workflowLogId: activeLogId });
    }
    hasFetchedLogOperationLogsRef.current.add(operationLogsKey);
  }, [logOutput, workflowId, activeLogId]);

  useEffect(() => {
    return () => {
      if (chartCancelRef.current && typeof chartCancelRef.current.cancel === 'function') {
        chartCancelRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  return null;
};

export default WorkflowLogsModalStateHandler;
